import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Rotating from './Rotating/Rotating';
import BrandsSkeleton from './BrandsSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './Brands.scss';

const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
};

const generateUniquePairs = (images, limit = null) => {
    if (!images?.length) return [];
    const shuffledImages = shuffleArray(images);

    let imagesToUse = shuffledImages;
    if (limit && limit < shuffledImages.length) {
        imagesToUse = shuffledImages.slice(0, limit);
    }
    
    return imagesToUse.reduce((pairs, image, index) => {
        if (index % 2 === 0) {
            pairs.push({
                front: image,
                back: imagesToUse[index + 1] || image
            });
        }
        return pairs;
    }, []);
};

const getItemsPerView = () => {
    const width = window.innerWidth;
    if (width <= 767) return 16;
    return 18;
};

const Brands = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { data: mainData, loading, error, refetch } = useCachedFetch('/data/locales/mainData.json');
    const [imagePairs, setImagePairs] = useState([]);
    const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const updateImagePairs = useCallback((logos) => {
        const pairs = generateUniquePairs(logos, itemsPerView);
        setImagePairs(pairs);
    }, [itemsPerView]);

    useEffect(() => {
        const handleResize = () => {
            const newItemsPerView = getItemsPerView();
            if (newItemsPerView !== itemsPerView) {
                setItemsPerView(newItemsPerView);
                if (mainData?.TopMainData?.AllLogoSection?.logos) {
                    updateImagePairs(mainData.TopMainData.AllLogoSection.logos);
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [itemsPerView, mainData, updateImagePairs]);

    useEffect(() => {
        const pageData = mainData?.TopMainData?.AllLogoSection;
        
        if (pageData?.logos) {
            updateImagePairs(pageData.logos);
            
            const intervalId = setInterval(() => {
                updateImagePairs(pageData.logos);
            }, 500000);

            return () => clearInterval(intervalId);
        }
    }, [mainData, updateImagePairs]);

    if (loading) return <BrandsSkeleton />;
    if (error) return <ErrorState onRetry={handleRetry} />;
    
    const mainContent = mainData?.TopMainData?.AllLogoSection;
    if (!mainContent) return <ErrorState onRetry={handleRetry} />;

    const { contentAccent, contentTitle, contentSubtitle } = mainContent;

    return (
        <div className='home__brands__section'>
            <div className='left__side__brands__section'>
                <div className='brands__content'>
                    <AnimatedOnScroll animation="fade-up" delay={0}>
                        <p className='brands__accent'>
                            {contentAccent?.[currentLang] || contentAccent?.en || ''}
                        </p>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={100}>
                        <h4 className='brands__title'>
                            {contentTitle?.[currentLang] || contentTitle?.en || ''}
                        </h4>
                    </AnimatedOnScroll>
                    <AnimatedOnScroll animation="fade-up" delay={200}>
                        <h5 className='brands__subtitle'>
                            {contentSubtitle?.[currentLang] || contentSubtitle?.en || ''}
                        </h5>
                    </AnimatedOnScroll>
                </div>
            </div>
            <div className='right__side__brands__section'>
                <div className='logos_wrapper'>
                    {imagePairs.map((pair, index) => (
                        <Rotating
                            key={`${pair.front.src}-${index}`}
                            frontImage={pair.front.src}
                            backImage={pair.back.src}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Brands;